<script lang='ts'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    // STYLING
    import 'tailwindcss/tailwind.css';

    // CASSIOPEIA
    import {
        Button,
        Heading,
        SESSION_USER,
        SESSION_ORGANIZATION,
        BidForm,
        Paragraph,
        Textarea,
        UserItem,
        OutputMobile,
        List,
        Separator,
        AuctionDownload,
        Link,
        EmbedVideo,
        Calendly,
    } from '@smatch/cassiopeia-web-components';

    // CONFIG
    import { DEFAULT, type Default } from './config.js';

    // UTILITY
    import merge from 'deepmerge';
    import { get } from 'svelte/store';

    // CONFIG
    import { CALENDLY_URL } from 'src/config.js';

    // TYPES
    import type { Bid } from 'src/app/types';

    /* PROPS ----------------------------------------------------------------------------------- */

    export let bid: Partial<Bid> = {};
    export let auctionKey: string | undefined;
    export let action: 'create' | 'update' = 'update';
    export let allowsPartial: boolean = false;

    /* INITIALIZE ------------------------------------------------------------------------------ */

    const ORGANIZATION = get(SESSION_ORGANIZATION);
    const USER = get(SESSION_USER);
    const MANAGER_KEY = ORGANIZATION?.managerKey;

    const overwriteMerge = (destinationArray: [], sourceArray: []): [] => sourceArray;

    const BID: Default = merge.all([
        DEFAULT,
        bid,
        {
            auctionKey,
            buyer: {
                organizationKey: ORGANIZATION?._.key,
                ownerKey: USER?._.key,
            },
        },
    ], { arrayMerge: overwriteMerge }) as Default;

    const BUYER = BID.buyer;

    /* INTERNALS -------------------------------------------------------------------------------- */

    let isSubmitted = false;

    const onSave = (): void => {
        isSubmitted = true;
        BUYER.description = undefined;
    };

    const beforeSave = (): void => {
        BUYER.description = `User requested callback: ${ BUYER.description ?? '' }`;
    };

</script>

{#if !isSubmitted }
    <!-- TODO: Fix finance options on PEG -->
    <BidForm { action } bid = { BID } on:save = { onSave } { beforeSave }>

        <Heading size = 'md'>
            { allowsPartial ? 'How to Purchase at smatch' : 'Get in Touch' }
        </Heading>

        {#if allowsPartial }
            <List mode = 'numeric'>
                <li>
                    <AuctionDownload auctionKey = { BID.auctionKey } let:data>
                        {#if Array.isArray(data) && data.length > 0}
                            {#each data as item}
                                <!-- TODO: Fix Pegasus API -->
                                <!-- eslint-disable @typescript-eslint/no-unsafe-member-access -->
                                <!-- Keep this check, to ensure if additional formats are added, this does not break -->
                                <!-- eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -->
                                {#if item.type === 'xlsx'}
                                    <Button
                                        label = 'Download'
                                        size = 'xxs'
                                        url = { item.url }
                                    />
                                {/if}
                                <!-- eslint-enable @typescript-eslint/no-unsafe-member-access -->
                            {/each}
                        {/if}
                    </AuctionDownload>
                    the auction file
                </li>
                <li>
                    Select items and add the price you are interested in purchasing
                </li>
                <li>
                    Send the file to
                    <Link
                        url = { 'mailto:buy@smatch.com' }
                        label = 'buy@smatch.com'
                        css = 's8-mail-to-link'
                    />
                    via e-mail
                </li>
                <li>
                    Our team will review your offer and reach out regarding next steps
                </li>
            </List>
            <Paragraph>Still unclear? Here's our video tutorial:</Paragraph>
            <EmbedVideo source = 'https://www.loom.com/embed/1fbc421630984a3da4cbdd59303dc0f7' />
            <Separator/>
        {:else }
            <EmbedVideo source = 'https://www.loom.com/embed/daebc066e6f64205aa9d1c0b9d1923b4' />
        {/if}

        <Textarea
            bind:value = { BUYER.description }
            maxLength = { 1000 }
            isRequired = { false }
            placeholder = 'Do you have questions about the offer? Request a Personal Call'
            class = 'min-h-[150px]'
            name = 'bid.description'
            label = { null }
        />

        <Button
            type = 'submit'
            css = 'w-full mt-1'
            align = 'left'
            label = 'Request a Personal Call'
        />

    </BidForm>

{:else}

    <div class = 'flex flex-col'>
        <Heading size = 'sm'>We'll get in back to you!</Heading>

        <div class = 'bg-grey-200 rounded-md p-3'>
            <Paragraph>
                Your account manager will reach out to discuss
                any questions you may have.
            </Paragraph>

            {#if USER !== null && MANAGER_KEY !== undefined}

                <UserItem key = { MANAGER_KEY } let:user>

                    <!-- TODO: Fix slot types -->
                    <!-- eslint-disable @typescript-eslint/no-unsafe-member-access -->
                    {#if USER.mobile !== undefined}
                        <Paragraph>Is this number up to date?</Paragraph>
                        <OutputMobile mobile = { USER.mobile }/>
                        <Paragraph css = 'mt-3'>
                            If not, please reach out to { user.name.first } { user.name.last }:
                        </Paragraph>
                    {:else}
                        <Paragraph>We will contact you per your email address.</Paragraph>
                        <Paragraph>You can also reach out to { user.name.first } { user.name.last }:</Paragraph>
                    {/if}
                    <OutputMobile mobile = { user.mobile }/>
                    <!-- eslint-enable -->

                </UserItem>

            {/if}

        </div>

    </div>
    <Calendly
        css = 'w-full mt-3'
        label = 'Create an Appointment'
        url = { CALENDLY_URL }
        mode = 'popup'
    />
{/if}

<style lang='postcss'>
    :global {
        .s8-mail-to-link {
            color: #44835F;
            text-decoration: underline;
        }
    }
</style>